import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { fetchAuthSession, signInWithRedirect, signOut as amplifySignOut } from 'aws-amplify/auth';
import { User } from '../Types/User';

const readStringClaim = (payload: Record<string, unknown>, claimNames: string[]): string => {
  for (const claimName of claimNames) {
    const claimValue = payload[claimName];
    if (typeof claimValue === 'string' && claimValue.trim()) {
      return claimValue.trim();
    }
  }

  return '';
};

const buildDisplayName = (payload: Record<string, unknown>): string => {
  const name = readStringClaim(payload, ['name', 'preferred_username', 'cognito:username', 'email']);
  if (name) {
    return name;
  }

  const givenName = readStringClaim(payload, ['given_name']);
  const familyName = readStringClaim(payload, ['family_name']);
  return [givenName, familyName].filter(Boolean).join(' ');
};

const buildEmail = (payload: Record<string, unknown>): string => {
  return readStringClaim(payload, ['email', 'preferred_username', 'upn']);
};

type AuthContextType = {
  user: User | null;
  isSignedIn: boolean;
  initialized: boolean;
  signIn: () => Promise<void>;
  signOut: () => void;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [initialized, setInitialized] = useState(false);

  const signIn = useCallback(async () => {
    setInitialized(true);
    await signInWithRedirect({ provider: { custom: process.env.REACT_APP_AUTH_PROVIDER! } });
  }, []);

  const signOut = useCallback(() => {
    amplifySignOut();
  }, []);

  const authenticate = useCallback(async () => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken.toString();
      const userPayload = session.tokens?.idToken?.payload;
      if (token && userPayload) {
        const payload = userPayload as Record<string, unknown>;
        setUser({
          displayName: buildDisplayName(payload),
          email: buildEmail(payload),
          roles: [],
        });
        setIsSignedIn(true);
      } else {
        await signIn();
      }
    } catch {
      await signIn();
    } finally {
      setInitialized(true);
    }
  }, [signIn]);

  useEffect(() => {
    authenticate();
  }, [authenticate]);

  return (
    <AuthContext.Provider value={{ user, isSignedIn, initialized, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
